import { Link, useRouterState } from "@tanstack/react-router";
import { navigationSections } from "@/shared/config/navigation";
import { usePermissions } from "@/entities/session/hooks/usePermissions";
import { hasAnyPermission } from "@/shared/config/permissionLogic";
import { useNavBadges } from "@/shared/hooks/useNavBadges";
import type { NavigationSection, NavigationSubItem } from "@/shared/types/navigationTypes";

export function SubNavBar() {
  const router = useRouterState();
  const pathname = router.location.pathname;
  const { hasPermission: checkPermission } = usePermissions();

  const activeSection = findActiveSection(pathname);
  const badges = useNavBadges({ enabled: !!activeSection?.subItems?.length });
  if (!activeSection?.subItems?.length) return null;

  const items = activeSection.subItems.filter(
    item => hasAnyPermission(checkPermission, item.permission),
  );
  if (items.length === 0) return null;

  const activeItemId = findActiveSubItemId(items, pathname);

  return (
    <div className="flex h-10 shrink-0 items-center gap-1 border-b border-line bg-surface px-5">
      {items.map(item => {
        const isActive = item.id === activeItemId;
        const badge = badges[item.id] ?? item.badge;
        return (
          <Link
            key={item.id}
            to={item.path}
            className={`relative inline-flex items-center gap-1.5 px-3 py-2 text-[13px] font-medium transition-colors ${
              isActive ? "text-ink" : "text-ink-3 hover:text-ink-2"
            }`}
          >
            {item.label}
            {badge !== undefined && (
              <span className="rounded-full bg-brand-accent-soft px-1.5 py-px text-[10px] font-semibold text-brand-accent-ink">
                {badge}
              </span>
            )}
            {isActive && (
              <span className="absolute inset-x-3 bottom-0 h-0.5 rounded-full bg-brand-accent" />
            )}
          </Link>
        );
      })}
    </div>
  );
}

function findActiveSection(pathname: string): NavigationSection | undefined {
  const exact = navigationSections.find(s => s.path === pathname);
  if (exact) return exact;

  const byPrefix = navigationSections.find(
    s => s.path && s.path !== "/" && pathname.startsWith(`${s.path}/`),
  );
  if (byPrefix) return byPrefix;

  return navigationSections.find(s =>
    s.subItems?.some(
      item => pathname === item.path || pathname.startsWith(`${item.path}/`),
    ),
  );
}

function findActiveSubItemId(items: NavigationSubItem[], pathname: string): string | undefined {
  const matches = items.filter(
    item => pathname === item.path || pathname.startsWith(`${item.path}/`),
  );
  if (matches.length === 0) return undefined;
  matches.sort((a, b) => b.path.length - a.path.length);
  return matches[0].id;
}
